import { Alert, Avatar, Box, Skeleton, Stack, Typography } from "@mui/material";
import { FoodsIcon, FoodsIllustrations, WhiteBox, iconMapping } from "@foodpilot/foods";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { SocialNotification, useSocialNotifications } from "src/api/client-api/social/useSocialNotifications";

const notificationIconByType: Record<SocialNotification["type"], keyof typeof iconMapping> = {
    post_created: "forum",
    comment_on_post: "sendMessage",
    reply_to_comment: "sendMessage",
    post_reaction: "reminder",
    comment_reaction: "reminder",
    mention: "userAccount",
};

export const SocialNotificationsPanel = () => {
    const { t } = useTranslation();
    const { data, isLoading, isError } = useSocialNotifications();

    return (
        <WhiteBox sx={{ p: 2 }}>
            <Stack gap={2}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <FoodsIcon icon="reminder" size={2} />
                    <Typography variant="h6">{t("social.notifications.title", "Recent activity")}</Typography>
                </Stack>
                {isLoading ?
                    <Box>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <Box key={index} display="flex" gap={1.5} py={1}>
                                <Skeleton variant="circular" width={24} height={24} />
                                <Box flex={1}>
                                    <Skeleton variant="text" width="80%" />
                                    <Skeleton variant="text" width="50%" />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                : isError ?
                    <Alert severity="info">
                        {t("social.notifications.error", "Unable to load recent forum activity.")}
                    </Alert>
                : !data?.notifications.length ?
                    <Stack alignItems="center" gap={1} py={2} textAlign="center">
                        <FoodsIllustrations illustration="informations" width={60} height={60} />
                        <Typography variant="body2" color="text.secondary">
                            {t("social.notifications.empty", "You're all caught up.")}
                        </Typography>
                    </Stack>
                :   <Stack gap={1.5}>
                        {data.notifications.map((notification) => (
                            <Stack
                                key={`${notification.type}-${notification.subject_id}-${notification.created_at}`}
                                direction="row"
                                gap={1.5}
                                alignItems="flex-start"
                            >
                                <Avatar sx={{ width: 28, height: 28 }}>
                                    <FoodsIcon icon={notificationIconByType[notification.type]} size={1} />
                                </Avatar>
                                <Box minWidth={0}>
                                    <Typography variant="body2">{formatNotificationTitle(notification, t)}</Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {notification.excerpt}
                                    </Typography>
                                </Box>
                            </Stack>
                        ))}
                    </Stack>
                }
            </Stack>
        </WhiteBox>
    );
};

const formatNotificationTitle = (notification: SocialNotification, t: TFunction) => {
    const actor = [notification.actor_firstname, notification.actor_lastname].filter(Boolean).join(" ");
    const actorName = actor || t("social.notifications.someone", "Someone");

    switch (notification.type) {
        case "post_created":
            return t("social.notifications.post_created", "{{actor}} published a new post", { actor: actorName });
        case "comment_on_post":
            return t("social.notifications.comment_on_post", "{{actor}} commented on your post", { actor: actorName });
        case "reply_to_comment":
            return t("social.notifications.reply_to_comment", "{{actor}} replied to your comment", {
                actor: actorName,
            });
        case "post_reaction":
            return t("social.notifications.post_reaction", "{{actor}} reacted to your post", { actor: actorName });
        case "comment_reaction":
            return t("social.notifications.comment_reaction", "{{actor}} reacted to your comment", {
                actor: actorName,
            });
        case "mention":
            return t("social.notifications.mention", "{{actor}} mentioned you", { actor: actorName });
    }
};
